import {
  CaseReducer,
  PayloadAction,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { Product } from "services/types/products.type";
import { getInitialStepsData } from "./utils/get-initial-steps-data.util";
import { findIndex, get, merge, set, sum } from "lodash";
import {
  CartItem,
  StepConfig,
  StepData,
  StepId,
  StepsDataState,
} from "./hearing-aids-wizard.type";
import { getSiblingStep } from "./utils/get-sibling-step.util";
import { getCartInfo } from "./utils/get-cart-info.util";
import { MANAGED_CARE_PAYMENT_SOURCE } from "constants/patients";
import { scrollToTop } from "./utils/scroll-to-top.util";

import {
  ACCESSORIES_STEP,
  BINAURAL_DOME_SUBSTEP,
  BINAURAL_EARMOLD_SUBSTEP,
  BINAURAL_HEARING_AID_SUBSTEP,
  BINAURAL_RECEIVER_SUBSTEP,
  BINAURAL_TUBE_SUBSTEP,
  CONFIGURATION_STEP,
  LEFT_DOME_SUBSTEP,
  LEFT_EARMOLD_SUBSTEP,
  LEFT_HEARING_AID_SUBSTEP,
  LEFT_RECEIVER_SUBSTEP,
  LEFT_TUBE_SUBSTEP,
  PRICING_STEP,
  RIGHT_DOME_SUBSTEP,
  RIGHT_EARMOLD_SUBSTEP,
  RIGHT_HEARING_AID_SUBSTEP,
  RIGHT_RECEIVER_SUBSTEP,
  RIGHT_TUBE_SUBSTEP,
  WARRANTIES_STEP,
} from "../constants";
import { EarHearingAidValues } from "../steps/hearing-aids-step/substeps/hearing-aids/hearing-aid-values.type";
import { EarDomeValues } from "../steps/hearing-aids-step/substeps/domes/dome-values.type";
import { EarReceiverValues } from "../steps/hearing-aids-step/substeps/receivers/receiver-values.type";
import { EarTubeValues } from "../steps/hearing-aids-step/substeps/tubes/tube-values.type";
import { normalizeFloatingNumberCorrection } from "utils/common";
import {
  D_OR_E_CONFIGURATION_EARMOLDS,
  EARS_CONFIGURATION_BOTH,
  EARS_CONFIGURATION_LEFT,
  EARS_CONFIGURATION_RIGHT,
  OrderConfigurationStepValues,
} from "../steps/order-configuration-step/order-configuration-step-values.type";
import { recalculateProductsPricesWithDistribution } from "utils/products-requests.utils";
import { getCartItemGroupActualPrices } from "./utils/get-cart-item.util";
import { ProductCatalogOptionValues } from "../steps/product-catalog-option-values.type";
import { getOrderCompletenessMessages } from "./utils/get-order-completeness-messages";

interface HearingAidsWizardState {
  currentStep?: StepConfig;
  stepsConfig: StepConfig[];
  stepsData: StepsDataState;
  syncToFormData: StepsDataState;
  // Which payment source the $0 prefill was last applied for, so switching away from
  // Managed Care mid-run undoes it instead of silently submitting a real sale at $0.
  managedCareZeroPricesAppliedFor?: number | null;
}

const initialState: HearingAidsWizardState = {
  stepsConfig: [],
  stepsData: {},
  syncToFormData: {},
  managedCareZeroPricesAppliedFor: null,
};

export const updateWizardConfigReducer: CaseReducer<
  HearingAidsWizardState,
  PayloadAction<StepConfig[]>
> = (state, action) => {
  const config = action.payload ?? [];
  const stepIndexes: Record<string, number> = {};

  state.stepsConfig = config
    .map((step, index) => {
      if (!step.parentId) {
        stepIndexes[step.id] = index;
        return { ...step, index };
      }
      return { ...step };
    })
    .map((step) => {
      if (step.parentId) {
        return { ...step, index: stepIndexes[step.parentId] };
      }
      return { ...step };
    });
  state.syncToFormData = {};
};

export const recalculateHearingAidsActualPriceReducer: CaseReducer<
  HearingAidsWizardState,
  PayloadAction<{ ear: "left" | "right" }>
> = (state, action) => {
  const { ear } = action.payload;
  const { stepsData } = state;

  const child_actual_prices = [];
  let hearing_aid_original_price: string | number | undefined = 0;

  if (ear === "left") {
    hearing_aid_original_price =
      (stepsData[LEFT_HEARING_AID_SUBSTEP] as EarHearingAidValues)?.left
        ?.hearing_aid?.price ??
      (stepsData[BINAURAL_HEARING_AID_SUBSTEP] as EarHearingAidValues)?.left
        ?.hearing_aid?.price;

    //domes
    child_actual_prices.push(
      (stepsData[LEFT_DOME_SUBSTEP] as EarDomeValues)?.left?.dome
        ?.component_price
    );
    child_actual_prices.push(
      (stepsData[BINAURAL_DOME_SUBSTEP] as EarDomeValues)?.left?.dome
        ?.component_price
    );

    //receivers
    child_actual_prices.push(
      (stepsData[LEFT_RECEIVER_SUBSTEP] as EarReceiverValues)?.left?.receiver
        ?.component_price
    );
    child_actual_prices.push(
      (stepsData[BINAURAL_RECEIVER_SUBSTEP] as EarReceiverValues)?.right
        ?.receiver?.component_price
    );

    //tubes
    child_actual_prices.push(
      (stepsData[LEFT_TUBE_SUBSTEP] as EarTubeValues)?.left?.tube
        ?.component_price
    );
    child_actual_prices.push(
      (stepsData[BINAURAL_TUBE_SUBSTEP] as EarTubeValues)?.left?.tube
        ?.component_price
    );
  } else if (ear === "right") {
    hearing_aid_original_price =
      (stepsData[RIGHT_HEARING_AID_SUBSTEP] as EarHearingAidValues)?.right
        ?.hearing_aid?.price ??
      (stepsData[BINAURAL_HEARING_AID_SUBSTEP] as EarHearingAidValues)?.right
        ?.hearing_aid?.price;

    //domes
    child_actual_prices.push(
      (stepsData[RIGHT_DOME_SUBSTEP] as EarDomeValues)?.right?.dome
        ?.component_price
    );
    child_actual_prices.push(
      (stepsData[BINAURAL_DOME_SUBSTEP] as EarDomeValues)?.right?.dome
        ?.component_price
    );

    //receivers
    child_actual_prices.push(
      (stepsData[RIGHT_RECEIVER_SUBSTEP] as EarReceiverValues)?.right?.receiver
        ?.component_price
    );

    child_actual_prices.push(
      (stepsData[BINAURAL_RECEIVER_SUBSTEP] as EarReceiverValues)?.left
        ?.receiver?.component_price
    );

    //tubes
    child_actual_prices.push(
      (stepsData[RIGHT_TUBE_SUBSTEP] as EarTubeValues)?.right?.tube
        ?.component_price
    );

    child_actual_prices.push(
      (stepsData[BINAURAL_TUBE_SUBSTEP] as EarTubeValues)?.right?.tube
        ?.component_price
    );
  }

  const original_price_with_components = String(
    normalizeFloatingNumberCorrection(
      sum(
        [...child_actual_prices, hearing_aid_original_price]
          .filter((price) => price)
          .map((price) => Number(price))
      )
    )
  );

  const earsConfig = (
    stepsData[CONFIGURATION_STEP] as OrderConfigurationStepValues
  ).ears;

  if (
    earsConfig === EARS_CONFIGURATION_LEFT &&
    get(stepsData, `${LEFT_HEARING_AID_SUBSTEP}.${ear}.hearing_aid`)
  ) {
    state.stepsData[LEFT_HEARING_AID_SUBSTEP] = {
      ...state.stepsData[LEFT_HEARING_AID_SUBSTEP],
      [ear]: {
        ...state.stepsData[LEFT_HEARING_AID_SUBSTEP][ear],
        original_price_with_components,
        actual_price: original_price_with_components,
      },
    };
  } else if (
    earsConfig === EARS_CONFIGURATION_RIGHT &&
    get(stepsData, `${RIGHT_HEARING_AID_SUBSTEP}.${ear}.hearing_aid`)
  ) {
    state.stepsData[RIGHT_HEARING_AID_SUBSTEP] = {
      ...state.stepsData[RIGHT_HEARING_AID_SUBSTEP],
      [ear]: {
        ...state.stepsData[RIGHT_HEARING_AID_SUBSTEP][ear],
        original_price_with_components,
        actual_price: original_price_with_components,
      },
    };
  } else if (
    earsConfig === EARS_CONFIGURATION_BOTH &&
    get(stepsData, `${BINAURAL_HEARING_AID_SUBSTEP}.${ear}.hearing_aid`)
  ) {
    state.stepsData[BINAURAL_HEARING_AID_SUBSTEP] = {
      ...state.stepsData[BINAURAL_HEARING_AID_SUBSTEP],
      [ear]: {
        ...state.stepsData[BINAURAL_HEARING_AID_SUBSTEP][ear],
        original_price_with_components,
        actual_price: original_price_with_components,
      },
    };
  }
};

const hearingAidsWizardSlice = createSlice({
  name: "hearing-aids-wizard",
  initialState,
  reducers: {
    resetWizard(state, action: PayloadAction<StepConfig[]>) {
      updateWizardConfigReducer(state, action);
      state.stepsData = getInitialStepsData(state.stepsConfig);
      state.currentStep = state.stepsConfig[0];
      state.managedCareZeroPricesAppliedFor = null;
    },
    updateWizardConfig(state, action: PayloadAction<StepConfig[]>) {
      updateWizardConfigReducer(state, action);
    },
    updateStepField(
      state,
      action: PayloadAction<{
        stepId?: StepId;
        name: string;
        value: any;
      }>
    ) {
      const { stepId, name, value } = action.payload;
      const stepLocator = (stepId || state.currentStep?.id) as StepId;

      if (!state.stepsData[stepLocator]) state.stepsData[stepLocator] = {};
      set(state.stepsData[stepLocator], name, value);
      state.syncToFormData[stepLocator] = state.stepsData[stepLocator];
    },
    setStepData(
      state,
      action: PayloadAction<{
        stepId?: StepId;
        value: any;
        dontRecalculateHearingAidsPrice?: boolean;
      }>
    ) {
      const { stepId, value, dontRecalculateHearingAidsPrice } = action.payload;
      const stepLocator = (stepId || state.currentStep?.id) as StepId;
      state.stepsData[stepLocator] = value ?? {};

      if (!dontRecalculateHearingAidsPrice) {
        recalculateHearingAidsActualPriceReducer(state, {
          payload: { ear: "left" },
          type: action.type,
        });

        recalculateHearingAidsActualPriceReducer(state, {
          payload: { ear: "right" },
          type: action.type,
        });
      }

      if (get(state.stepsData, `${ACCESSORIES_STEP}.products`)?.length) {
        const products = get(
          state.stepsData,
          `${ACCESSORIES_STEP}.products`
        ) as ProductCatalogOptionValues[];

        products.forEach((product, index) => {
          state.stepsData[ACCESSORIES_STEP] = {
            ...state.stepsData[ACCESSORIES_STEP],
            products: products.map((product, index) => ({
              ...product,
              actual_price: String(
                Number(
                  product.use_component_price
                    ? product.component_price || 0
                    : product.price
                ) * Number(product.quantity || 0)
              ),
            })),
          };
        });
      }
    },
    setSyncToFormData(
      state,
      action: PayloadAction<{ stepId?: StepId; value: any }>
    ) {
      const { stepId, value } = action.payload;
      const stepLocator = (stepId || state.currentStep?.id) as StepId;
      state.syncToFormData[stepLocator] = value;
    },
    prevStep(state, _action: PayloadAction<void>) {
      state.currentStep = getSiblingStep(
        state.stepsConfig,
        state.currentStep,
        -1
      );
      scrollToTop();
    },
    nextStep(state, _action: PayloadAction<void>) {
      state.currentStep = getSiblingStep(
        state.stepsConfig,
        state.currentStep,
        1
      );
      scrollToTop();
    },
    gotoStep(state, action: PayloadAction<StepId>) {
      const stepId = action.payload;
      state.currentStep = state.stepsConfig.find((step) => step.id === stepId);
      scrollToTop();
    },
    gotoFirstSubStep(state, action: PayloadAction<StepId>) {
      const stepId = action.payload;
      const step = state.stepsConfig.find((step) => step.id === stepId);
      const subStepId = step?.substeps[0];

      if (subStepId) {
        state.currentStep = state.stepsConfig.find(
          (step) => step.id === subStepId
        );
      } else {
        state.currentStep = step;
      }
      scrollToTop();
    },
    removeProduct(
      state,
      action: PayloadAction<{
        stepId: StepId;
        productFieldName: string;
        productId: number;
      }>
    ) {
      const { stepId, productFieldName, productId } = action.payload;

      let result = get(state.stepsData[stepId], productFieldName) as
        | Product
        | Product[]
        | undefined
        | null;

      state.syncToFormData = {};

      if (Array.isArray(result)) {
        const index = findIndex(result, (product) => product.id === productId);
        result.splice(index, 1);
      } else if (result && result.id === productId) {
        state.stepsData[stepId][productFieldName] = null;
      }
      state.syncToFormData[stepId] = state.stepsData[stepId];
    },
    recalculateActualPrices(
      state,
      action: PayloadAction<{ actual_price: string }>
    ) {
      const { stepsData } = state;

      const prevTotalActualPrice = action.payload.actual_price || "0";

      const { hearingAids, earmolds, accessories, warranties, earsConfig } =
        getCartInfo(stepsData);

      const prevPrices: string[] = [
        ...getCartItemGroupActualPrices(hearingAids),
        ...getCartItemGroupActualPrices(earmolds),
        ...accessories.map((item) => String(item.product.actual_price)),
        ...warranties.map((item) => String(item.product.actual_price)),
      ];

      const newPrices = recalculateProductsPricesWithDistribution(
        prevPrices,
        String(prevTotalActualPrice)
      ).map((value) => value.toString());

      const syncBuffer: StepData = {};
      if (earsConfig === EARS_CONFIGURATION_BOTH) {
        set(
          syncBuffer,
          `${BINAURAL_HEARING_AID_SUBSTEP}.left.actual_price`,
          newPrices[0]
        );
        set(
          syncBuffer,
          `${BINAURAL_HEARING_AID_SUBSTEP}.right.actual_price`,
          newPrices[1]
        );

        if (
          (state.stepsData[CONFIGURATION_STEP] as OrderConfigurationStepValues)
            .domesOrEarmolds === D_OR_E_CONFIGURATION_EARMOLDS
        ) {
          set(
            syncBuffer,
            `${BINAURAL_EARMOLD_SUBSTEP}.left.actual_price`,
            newPrices[2]
          );
          set(
            syncBuffer,
            `${BINAURAL_EARMOLD_SUBSTEP}.right.actual_price`,
            newPrices[3]
          );
        }
      } else if (earsConfig === EARS_CONFIGURATION_LEFT) {
        set(
          syncBuffer,
          `${LEFT_HEARING_AID_SUBSTEP}.left.actual_price`,
          newPrices[0]
        );
        if (
          (state.stepsData[CONFIGURATION_STEP] as OrderConfigurationStepValues)
            .domesOrEarmolds === D_OR_E_CONFIGURATION_EARMOLDS
        ) {
          set(
            syncBuffer,
            `${LEFT_EARMOLD_SUBSTEP}.left.actual_price`,
            newPrices[2]
          );
        }
      } else if (earsConfig === EARS_CONFIGURATION_RIGHT) {
        set(
          syncBuffer,
          `${RIGHT_HEARING_AID_SUBSTEP}.right.actual_price`,
          newPrices[1]
        );
        if (
          (state.stepsData[CONFIGURATION_STEP] as OrderConfigurationStepValues)
            .domesOrEarmolds === D_OR_E_CONFIGURATION_EARMOLDS
        ) {
          set(
            syncBuffer,
            `${RIGHT_EARMOLD_SUBSTEP}.right.actual_price`,
            newPrices[3]
          );
        }
      }

      const accessoriesMinIndex = 4;
      const accessoriesMaxIndex = accessoriesMinIndex + accessories.length - 1;

      const warrantiesMinIndex = accessoriesMaxIndex + 1;
      const warrantiesMaxIndex = warrantiesMinIndex + warranties.length;

      const accessoriesPrices = newPrices.filter(
        (_, index) =>
          index >= accessoriesMinIndex && index <= accessoriesMaxIndex
      );

      accessories.forEach((item, index) => {
        set(
          syncBuffer,
          `${ACCESSORIES_STEP}.${item.product.productFieldName}.actual_price`,
          accessoriesPrices[index]
        );
      });

      const warrantiesPrices = newPrices.filter(
        (_, index) => index >= warrantiesMinIndex && index <= warrantiesMaxIndex
      );

      warranties.forEach((item, index) => {
        set(
          syncBuffer,
          `${WARRANTIES_STEP}.${item.product.productFieldName}.actual_price`,
          warrantiesPrices[index]
        );
      });

      state.stepsData = merge({}, stepsData, syncBuffer);
      state.syncToFormData = syncBuffer;
    },
    // Managed Care: the practice is paid a fitting fee, not device prices, so device/earmold/
    // accessory prices prefill to $0 (Intercom 215475325068826). Warranties are service-type
    // lines and keep their prices; commissions need no special-casing because they compute on
    // price. Every price stays editable after the prefill.
    //
    // Keyed on the payment source rather than a boolean so that going Back and switching away
    // from Managed Care UNDOES the prefill (restoring each line to its full price) instead of
    // leaving a real private-pay invoice sitting at $0.
    syncManagedCarePrices(
      state,
      action: PayloadAction<{ paymentSourceType?: number | null }>
    ) {
      const { paymentSourceType } = action.payload;
      const isManagedCare = paymentSourceType === MANAGED_CARE_PAYMENT_SOURCE;
      const wasApplied =
        state.managedCareZeroPricesAppliedFor === MANAGED_CARE_PAYMENT_SOURCE;

      // Nothing to do: already prefilled, or never was and still is not Managed Care.
      if (isManagedCare === wasApplied) return;

      const { hearingAids, earmolds, accessories } = getCartInfo(state.stepsData);
      const syncBuffer: StepData = {};

      // Zeroing: "0". Undo: back to the line's full price (the prefill overwrote whatever
      // was there, so its full price is the only sane inverse).
      const priceFor = (item: CartItem) =>
        isManagedCare
          ? "0"
          : String(
              item.product.original_price_with_components ||
                item.product.original_price * item.product.quantity
            );

      [...hearingAids, ...earmolds].forEach((item) => {
        if (!item.ear) return;
        set(
          syncBuffer,
          `${item.stepId}.${item.ear}.actual_price`,
          priceFor(item)
        );
      });

      accessories.forEach((item) => {
        set(
          syncBuffer,
          `${ACCESSORIES_STEP}.${item.product.productFieldName}.actual_price`,
          priceFor(item)
        );
      });

      state.stepsData = merge({}, state.stepsData, syncBuffer);

      const cart = getCartInfo(state.stepsData);
      syncBuffer[PRICING_STEP] = { actual_price: String(cart.actual_price_sum) };
      state.stepsData[PRICING_STEP] = syncBuffer[PRICING_STEP];

      state.syncToFormData = syncBuffer;
      state.managedCareZeroPricesAppliedFor = isManagedCare
        ? MANAGED_CARE_PAYMENT_SOURCE
        : null;
    },
    updateTotalActualPrice(state, action: PayloadAction<void>) {
      const cart = getCartInfo(state.stepsData);

      const syncBuffer: StepData = { actual_price: cart.actual_price_sum };

      state.stepsData[PRICING_STEP] = syncBuffer;
      state.syncToFormData[PRICING_STEP] = syncBuffer;
    },
    resetStep(
      state,
      action: PayloadAction<{ stepId?: StepId; initialData?: StepData }>
    ) {
      const { stepId, initialData } = action.payload;
      const stepLocator = (stepId || state.currentStep?.id) as StepId;
      if (!state.stepsData[stepLocator]) {
        state.stepsData[stepLocator] = {};
      }
      set(state.stepsData, stepLocator, initialData);
      state.syncToFormData[stepLocator] = {};
    },
  },
});

const getCurrentStep = (state: RootState) =>
  state.hearingAidsWizard.currentStep;
const getStepsData = (state: RootState) => state.hearingAidsWizard.stepsData;
const getSyncToFormData = (state: RootState) =>
  state.hearingAidsWizard.syncToFormData;
const stepIdParam = (_: RootState, stepId?: StepId) => stepId;
const fieldParam = (_: RootState, path: { stepId?: StepId; name: string }) =>
  path;

const getStepsConfig = (state: RootState) =>
  state.hearingAidsWizard.stepsConfig;

export const selectStepsConfig = createSelector(
  [getStepsConfig, stepIdParam],
  (stepsConfig, stepId) => {
    if (stepId) {
      return stepsConfig.find((step) => step.id === stepId);
    } else {
      return stepsConfig;
    }
  }
);

export const selectOrderCompleteness = createSelector(
  [getStepsData],
  (stepsData) => getOrderCompletenessMessages(stepsData)
);

export const selectStepsData = createSelector(
  [getStepsData],
  (stepsData) => stepsData
);
export const selectStepData = createSelector(
  [getCurrentStep, getStepsData, stepIdParam],
  (currentStep, stepsData, stepId) => {
    return !stepId ? stepsData[String(currentStep?.id)] : stepsData[stepId];
  }
);

export const selectSyncToFormData = createSelector(
  [getCurrentStep, getSyncToFormData, stepIdParam],
  (currentStep, syncToFormData, stepId) => {
    return !stepId
      ? syncToFormData[String(currentStep?.id)]
      : syncToFormData[stepId];
  }
);

export const selectStepField = createSelector(
  [getCurrentStep, getStepsData, fieldParam],
  (currentStep, stepsData, fieldParam) => {
    const { stepId, name } = fieldParam;
    if (stepId) {
      return get(stepsData[stepId], name);
    } else if (currentStep?.id) {
      return get(stepsData[currentStep.id], name);
    }
  }
);

export const selectCurrentStep = createSelector(
  getCurrentStep,
  (currentStep) => {
    return currentStep;
  }
);

export const selectRootSteps = createSelector(getStepsConfig, (stepsConfig) => {
  return stepsConfig.filter((step: StepConfig) => !step.parentId);
});

export const selectCart = createSelector(
  getStepsData,
  (stepsData: Record<StepId, StepData>) => getCartInfo(stepsData)
);

export const {
  resetWizard,
  updateWizardConfig,
  updateStepField,
  setStepData,
  setSyncToFormData,
  removeProduct,
  prevStep,
  nextStep,
  gotoStep,
  gotoFirstSubStep,
  resetStep,
  updateTotalActualPrice,
  recalculateActualPrices,
  syncManagedCarePrices,
} = hearingAidsWizardSlice.actions;

export default hearingAidsWizardSlice.reducer;
